import _ from 'lodash';
import moment from 'moment';
import enabled from 'data/enabled.json';

// สมัครล่าช้า TGAT/TPAT, A-Level, รอบ 3 (ทปอ. 17 ต.ค. 69) — ค่าสมัครเท่าเดิม + ค่าธรรมเนียม 200 บาทต่อการยืนยัน 1 ครั้ง
// เช่น TPAT 2 วิชา = 140×2+200 = 480 · ต่อมาเพิ่ม 1 วิชา = 140+200 = 340 · ใช้สิทธิ์ อว. ได้ตามปกติ (ได้สิทธิ์ตอนจ่าย)
// กติกาเดียวกับ BE tcas65-student-api helpers/late-fee.js:
// - 200 เก็บทุกการยืนยันในช่วงล่าช้า แม้ อว. จ่ายค่าวิชาให้ครบ
// - ใบช่วงปกติที่ค้างจ่าย = หมดอายุเมื่อเข้าช่วงล่าช้า → ยืนยันใหม่ (ชุดเดิมได้) ได้ใบใหม่ +200
// - ใบช่วงล่าช้าที่ค้างจ่าย = ยืนยันเพิ่มไม่ได้จนกว่าจะจ่าย (pay-first)
export const LATE_FEE = 200;
export const LATE_PAY_FIRST = 'กรุณาชำระเงินค่าสมัครล่าช้าที่ค้างอยู่ก่อนยืนยันการสมัครเพิ่ม';

const at = (timestamp) => moment(typeof timestamp === 'number' ? timestamp : Date.parse(timestamp));

// สมัครสอบ (enabled.tgattpat_apply_exams / alevel_apply_exams): 'normal' ถึง expired · 'late' ถึง late_payment_expired · 'closed'
// gate จริงอยู่ที่ BE timewindows (FE ต้องเป็น subset — e2e 00-consistency/time-windows.spec.js)
export const applyExamPhase = (windowKey, timestamp) => {
  const { expired, late_payment_expired } = enabled[windowKey];
  if (!at(timestamp).isAfter(expired)) return 'normal';
  if (!at(timestamp).isAfter(late_payment_expired)) return 'late';
  return 'closed';
};

// รอบ 3 (enabled.round3): ก่อน apply_start = null · 'normal' ก่อน late_start · 'late' ถึง apply_end · 'closed'
export const round3ApplyPhase = (timestamp) => {
  const { apply_start, late_start, apply_end } = enabled.round3;
  if (at(timestamp).isBefore(apply_start)) return null;
  if (at(timestamp).isBefore(late_start)) return 'normal';
  if (!at(timestamp).isAfter(apply_end)) return 'late';
  return 'closed';
};

export const lateFeeFor = (phase) => (phase === 'late' ? LATE_FEE : 0);

// แถว history_logs ของ /applicants/me/apply-exam-summary (order_late_fee รวมอยู่ใน order_amount แล้ว)
const lateFeeOfLog = (log) => Number(_.get(log, 'order_late_fee')) || 0;
export const isLatePendingLog = (log) => _.get(log, 'order_status') === 'pending' && lateFeeOfLog(log) > 0;
// ใบช่วงปกติที่ยังค้างตอนเข้าช่วงล่าช้า — จ่ายไม่ได้แล้ว
export const isExpiredPendingLog = (log, phase) => phase === 'late' && _.get(log, 'order_status') === 'pending' && !lateFeeOfLog(log);
// ค่าธรรมเนียมล่าช้าของใบที่ค้างอยู่ (หลายวันอยู่ใบเดียวกัน — นับต่อ order_id)
export const pendingLateFee = (logs = []) => _.sumBy(_.uniqBy(logs.filter(isLatePendingLog), 'order_id'), lateFeeOfLog);

// รอบ 3: แถว /applicants/me/admissions (Admission.late_fee แยกจาก payment_amount — ใบ = payment_amount + late_fee · status = สถานะใบ)
export const admissionLateFee = (admission) => Number(_.get(admission, 'late_fee')) || 0;
export const isLatePendingAdmission = (admission) => _.get(admission, 'status') === 'pending' && admissionLateFee(admission) > 0;
export const isExpiredPendingAdmission = (admission, phase) =>
  phase === 'late' && _.get(admission, 'status') === 'pending' && !admissionLateFee(admission);
// ยืนยันครั้งที่ 3 ใหม่ได้โดยไม่ต้อง Set Zero (BE helpers/round3-confirm.js replacesWithdrawn):
// ใบถูกยกเลิกเพราะถอนหลักสูตร (R27-2) หรือใบช่วงปกติหมดอายุในช่วงล่าช้า
export const replacesThirdConfirmation = (admission, phase) => _.get(admission, 'confirm_id') >= 3
  && (_.get(admission, 'cancel_reason') === 'program_project_deleted' || isExpiredPendingAdmission(admission, phase));
// ทปอ. (อ.ชาลี 3 พ.ย. 69) รอบ 3 ช่วงล่าช้า: มีอันดับที่มีผลแล้ว (status 'paid' = จ่ายแล้วหรือ อว. จ่ายครบ) → ต้องเพิ่มสาขาใหม่
// และคงสาขาเดิมครบ (สลับลำดับเดิมได้เมื่อเพิ่ม — Komthep 3 พ.ย. 69) · Set Zero ไม่ได้ — คู่กับ BE helpers/round3-confirm lateRankingAllowed
export const LATE_R3_ADD_ONLY = 'ช่วงสมัครล่าช้าไม่สามารถสลับหรือลดอันดับเดิมได้ — ยืนยันได้เมื่อเพิ่มสาขาวิชาใหม่และคงสาขาวิชาเดิมไว้ครบทุกสาขา';
export const lateRankingAllowed = (admissions = [], projects = []) => {
  const secured = _.findLast(admissions, ({ set_zero, status }) => !set_zero && status === 'paid');
  if (!secured) return true;
  const kept = (secured.program_projects || []).map(({ _id }) => String(_id).toLowerCase());
  const next = projects.map(({ program_project }) => String(program_project).toLowerCase());
  return next.length > kept.length && kept.every((id) => next.includes(id));
};
// โควตายืนยันรอบ 3 ที่ใช้ไปจริง (หัวใจ / "ยืนยันได้อีก N ครั้ง") — ครั้งที่ 3 ที่ยืนยันแทนได้ (replacesThirdConfirmation)
// ไม่นับ เพราะ BE ให้ยืนยันซ้ำเป็นครั้งที่ 3 เดิม · เดิมนับ admissions.length ตรง ๆ = ขึ้น "อีก 0 ครั้ง" ทั้งที่ยืนยันได้
export const round3QuotaUsed = (admissions, phase) => {
  const list = admissions || [];
  return Math.max(0, list.length - (replacesThirdConfirmation(_.last(list), phase) ? 1 : 0));
};
